import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function QuizFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, active, ended, paid, free
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Estados para quiz em progresso
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [quizStartTime, setQuizStartTime] = useState(null);
  
  // Estados para resultados
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [showAnswerDetails, setShowAnswerDetails] = useState(false);
  
  const [editingPost, setEditingPost] = useState(null);

  const handleEditPost = (post) => {
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setQuizzes((prev) => prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)));
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setQuizzes((prev) => prev.filter((p) => p._id !== postId));
  };

  const categories = [
    { value: "", label: "Todas Categorias" },
    { value: "general", label: "Conhecimentos Gerais" },
    { value: "educational", label: "Educacional" },
    { value: "entertainment", label: "Entretenimento" },
    { value: "professional", label: "Profissional" },
    { value: "trivia", label: "Trivia" },
    { value: "science", label: "Ciência" },
    { value: "history", label: "História" },
    { value: "technology", label: "Tecnologia" },
    { value: "sports", label: "Esportes" },
  ];

  const fetchQuizzes = async (pageNum = 1) => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/quiz?page=${pageNum}&limit=12`;
      if (filter !== "all") url += `&filter=${filter}`;
      if (category) url += `&category=${category}`;

      const { data } = await axios.get(url, { 
        headers: { Authorization: `Bearer ${user.token}` } 
      });
      
      if (pageNum === 1) {
        setQuizzes(data.quizzes || []);
      } else {
        setQuizzes((prev) => [...prev, ...(data.quizzes || [])]);
      }
      
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Erro ao buscar quizzes:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchQuizzes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, category]);

  // Timer do quiz
  useEffect(() => {
    if (!quizStarted || !timeRemaining || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmitQuiz(); // Auto-submit ao acabar o tempo
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizStarted, timeRemaining]);

  const joinQuiz = async (quiz) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/quiz/${quiz._id}/join`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      startQuiz(quiz);
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao participar do quiz");
    }
  };

  const startQuiz = async (quiz) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/quiz/${quiz._id}/start`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      setSelectedQuiz(data);
      setQuizStarted(true);
      setCurrentQuestion(0);
      setUserAnswers([]);
      setQuizStartTime(new Date(data.startedAt));
      
      if (data.timeLimit) {
        setTimeRemaining(data.timeLimit * 60); // Converter minutos para segundos
      }
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao iniciar quiz");
    }
  };

  const handleSelectAnswer = (answer) => {
    const newAnswers = [...userAnswers];
    const startTime = new Date();
    
    newAnswers[currentQuestion] = {
      questionIndex: currentQuestion,
      answer,
      timeSpent: quizStartTime ? Math.floor((startTime - quizStartTime) / 1000) : 0,
    };
    
    setUserAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < selectedQuiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/quiz/${selectedQuiz._id}/submit`,
        { answers: userAnswers },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      setResults(data);
      setShowResults(true);
      setQuizStarted(false);
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao submeter quiz");
    }
  };

  const viewResults = async (quizId) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/quiz/${quizId}/results`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      
      setResults(data);
      setShowResults(true);
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao buscar resultados");
    }
  };

  const closeQuiz = () => {
    setSelectedQuiz(null);
    setQuizStarted(false);
    setCurrentQuestion(0);
    setUserAnswers([]);
    setTimeRemaining(null);
    setQuizStartTime(null);
    setShowResults(false);
    setShowAnswerDetails(false);
    setResults(null);
    fetchQuizzes(1); // Recarregar quizzes
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading && quizzes.length === 0) {
    return (
      <div className="quiz_feed_loading">
        <div className="quiz_loader"></div>
        <span>Carregando quizzes...</span>
      </div>
    );
  }

  // Renderizar quiz em progresso
  if (quizStarted && selectedQuiz) {
    const currentQ = selectedQuiz.questions[currentQuestion];
    const progress = ((currentQuestion + 1) / selectedQuiz.questions.length) * 100;

    return (
      <div className="quiz_playing">
        <div className="quiz_header">
          <h2>{selectedQuiz.title}</h2>
          <button className="quiz_close_btn" onClick={closeQuiz}>✕</button>
        </div>

        <div className="quiz_progress_bar">
          <div className="quiz_progress_fill" style={{ width: `${progress}%` }}></div>
        </div>

        <div className="quiz_info">
          <span>Pergunta {currentQuestion + 1} de {selectedQuiz.questions.length}</span>
          {timeRemaining !== null && (
            <span className="quiz_timer">{formatTime(timeRemaining)}</span>
          )}
        </div>

        <div className="quiz_question_card">
          {currentQ.imageUrl && (
            <img src={currentQ.imageUrl} alt="Question" className="quiz_question_image" />
          )}
          <h3>{currentQ.question}</h3>
          <div className="quiz_points">+{currentQ.points} pontos</div>

          <div className="quiz_answers">
            {currentQ.type === "multiple_choice" && currentQ.options?.map((option, idx) => (
              <button
                key={idx}
                className={`quiz_answer_btn ${userAnswers[currentQuestion]?.answer === option ? 'selected' : ''}`}
                onClick={() => handleSelectAnswer(option)}
              >
                {option}
              </button>
            ))}

            {currentQ.type === "true_false" && (
              <>
                <button
                  className={`quiz_answer_btn ${userAnswers[currentQuestion]?.answer === true ? 'selected' : ''}`}
                  onClick={() => handleSelectAnswer(true)}
                >
                  Verdadeiro
                </button>
                <button
                  className={`quiz_answer_btn ${userAnswers[currentQuestion]?.answer === false ? 'selected' : ''}`}
                  onClick={() => handleSelectAnswer(false)}
                >
                  Falso
                </button>
              </>
            )}

            {currentQ.type === "text" && (
              <input
                type="text"
                className="quiz_text_input"
                value={userAnswers[currentQuestion]?.answer || ''}
                onChange={(e) => handleSelectAnswer(e.target.value)}
                placeholder="Digite sua resposta..."
              />
            )}
          </div>
        </div>

        <div className="quiz_navigation">
          <button
            className="quiz_nav_btn"
            onClick={prevQuestion}
            disabled={currentQuestion === 0}
          >
            ← Anterior
          </button>

          {currentQuestion < selectedQuiz.questions.length - 1 ? (
            <button className="quiz_nav_btn quiz_next" onClick={nextQuestion}>
              Próxima →
            </button>
          ) : (
            <button className="quiz_nav_btn quiz_submit" onClick={handleSubmitQuiz}>
              Finalizar Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  // Renderizar resultados
  if (showResults && results) {
    const percentage = Math.round((results.userResults.correctAnswers / results.userResults.totalQuestions) * 100);
    const isPassed = percentage >= 60; // Considerar 60% como passou
    
    return (
      <div className="quiz_results">
        <div className="quiz_results_header">
          <h2>🎯 Resultado do Quiz</h2>
          <button className="quiz_close_btn" onClick={closeQuiz}>✕</button>
        </div>

        {/* PARABÉNS / RESULTADO */}
        <div className={`quiz_result_celebration ${isPassed ? 'success' : 'incomplete'}`}>
          {isPassed && (
            <>
              <div className="celebration_icon">🎉</div>
              <h3>Parabéns!</h3>
              <p>Você completou o quiz com sucesso!</p>
            </>
          )}
          {!isPassed && (
            <>
              <div className="celebration_icon">📊</div>
              <h3>Quiz Completado</h3>
              <p>Continue se desenvolvendo para melhorar seus resultados!</p>
            </>
          )}
        </div>

        {/* SCORE CARD */}
        <div className="quiz_score_card">
          <div className="quiz_score_circle">
            <span className="quiz_score_value">{percentage}%</span>
            <span className="quiz_score_label">Desempenho</span>
          </div>

          <div className="quiz_score_details">
            <div className="score_detail_item">
              <span className="detail_label">Pontos:</span>
              <span className="detail_value highlight">
                {results.userResults.score} pontos
              </span>
            </div>

            <div className="score_detail_item">
              <span className="detail_label">Acertos:</span>
              <span className="detail_value">
                {results.userResults.correctAnswers} / {results.userResults.totalQuestions}
              </span>
            </div>

            <div className="score_detail_item">
              <span className="detail_label">Taxa de Acerto:</span>
              <span className={`detail_value ${percentage >= 80 ? 'excellent' : percentage >= 60 ? 'good' : 'fair'}`}>
                {percentage}%
              </span>
            </div>

            <div className="score_detail_item">
              <span className="detail_label">Tempo Total:</span>
              <span className="detail_value">
                {formatTime(results.userResults.timeElapsed)}
              </span>
            </div>

            {results.userResults.rank && (
              <div className="score_detail_item ranking">
                <span className="detail_label">🏆 Sua Posição:</span>
                <span className="detail_value rank_badge">#{results.userResults.rank}</span>
              </div>
            )}

            {results.userResults.prizeWon > 0 && (
              <div className="score_detail_item prize_won">
                <span className="detail_label">💰 Prêmio Ganho:</span>
                <span className="detail_value prize_amount">${results.userResults.prizeWon} USD</span>
              </div>
            )}
          </div>
        </div>

        {/* RANKING GERAL */}
        {results.leaderboard && results.leaderboard.length > 0 && (
          <div className="quiz_leaderboard">
            <h3>🏅 Ranking Geral</h3>
            <div className="leaderboard_list">
              {results.leaderboard.map((entry, idx) => (
                <div 
                  key={idx} 
                  className={`leaderboard_item ${entry.rank === results.userResults.rank ? 'current_user' : ''}`}
                >
                  <span className="leaderboard_rank">#{entry.rank}</span>
                  
                  {/* Medalha para top 3 */}
                  {entry.rank === 1 && <span className="medal">🥇</span>}
                  {entry.rank === 2 && <span className="medal">🥈</span>}
                  {entry.rank === 3 && <span className="medal">🥉</span>}

                  <img
                    src={entry.user.picture}
                    alt={entry.user.username}
                    className="leaderboard_avatar"
                  />
                  
                  <div className="leaderboard_user_info">
                    <span className="leaderboard_name">{entry.user.first_name} {entry.user.last_name}</span>
                    <span className="leaderboard_username">@{entry.user.username}</span>
                  </div>

                  <div className="leaderboard_stats">
                    <span className="leaderboard_score">{entry.score} pts</span>
                    {entry.prizeWon > 0 && (
                      <span className="leaderboard_prize">💰 ${entry.prizeWon}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ESTATÍSTICAS GERAIS */}
        <div className="quiz_stats_summary">
          <h4>📊 Estatísticas do Quiz</h4>
          <div className="stats_grid">
            <div className="stat_box">
              <span className="stat_label">Total de Participantes:</span>
              <span className="stat_value">{results.stats.totalParticipants}</span>
            </div>
            <div className="stat_box">
              <span className="stat_label">Concluíram:</span>
              <span className="stat_value">{results.stats.completions}</span>
            </div>
            <div className="stat_box">
              <span className="stat_label">Média de Acertos:</span>
              <span className="stat_value">{Math.round(results.stats.averageScore)}%</span>
            </div>
            <div className="stat_box">
              <span className="stat_label">Melhor Score:</span>
              <span className="stat_value">{results.stats.highestScore} pts</span>
            </div>
          </div>
        </div>

        {/* BOTÕES DE AÇÃO */}
        <div className="quiz_results_actions">
          <button className="quiz_back_btn" onClick={closeQuiz}>
            ← Voltar ao Feed
          </button>
          <button 
            className="quiz_view_answers_btn" 
            onClick={() => setShowAnswerDetails(true)}
          >
            📋 Ver Respostas Detalhadas
          </button>
        </div>
      </div>
    );
  }

  // Renderizar detalhes de respostas
  if (showAnswerDetails && results && results.userResults.answers) {
    return (
      <div className="quiz_answer_details">
        <div className="answer_details_header">
          <h2>📋 Detalhes das Respostas</h2>
          <button className="quiz_close_btn" onClick={() => setShowAnswerDetails(false)}>✕</button>
        </div>

        <div className="answer_details_summary">
          <span className="summary_item">
            Acertos: <strong>{results.userResults.correctAnswers}/{results.userResults.totalQuestions}</strong>
          </span>
          <span className="summary_item">
            Taxa: <strong>{Math.round((results.userResults.correctAnswers / results.userResults.totalQuestions) * 100)}%</strong>
          </span>
          <span className="summary_item">
            Pontos: <strong>{results.userResults.score}</strong>
          </span>
        </div>

        <div className="answers_list">
          {results.userResults.answers.map((answer, idx) => (
            <div 
              key={idx} 
              className={`answer_card ${answer.isCorrect ? 'correct' : 'incorrect'}`}
            >
              {/* Número da pergunta e status */}
              <div className="answer_header">
                <span className="question_number">Pergunta {idx + 1}</span>
                <span className={`answer_status ${answer.isCorrect ? 'correct_mark' : 'incorrect_mark'}`}>
                  {answer.isCorrect ? '✓ Acertou' : '✗ Errou'}
                </span>
                <span className="points_badge">+{answer.pointsEarned} pontos</span>
              </div>

              {/* Pergunta */}
              <div className="question_section">
                <h4>Pergunta:</h4>
                <p>{answer.question}</p>
                {answer.imageUrl && (
                  <img src={answer.imageUrl} alt="Question" className="answer_question_image" />
                )}
              </div>

              {/* Resposta do usuário vs Correta */}
              <div className="comparison_section">
                <div className="answer_given">
                  <span className="label">Sua Resposta:</span>
                  <div className={`value ${answer.isCorrect ? 'correct' : 'wrong'}`}>
                    {answer.type === "true_false" ? (
                      answer.userAnswer === true ? "Verdadeiro" : "Falso"
                    ) : (
                      answer.userAnswer
                    )}
                  </div>
                </div>

                {!answer.isCorrect && (
                  <div className="answer_correct">
                    <span className="label">Resposta Correta:</span>
                    <div className="value correct">
                      {answer.type === "true_false" ? (
                        answer.correctAnswer === true ? "Verdadeiro" : "Falso"
                      ) : (
                        answer.correctAnswer
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Explicação */}
              {answer.explanation && (
                <div className="explanation_section">
                  <span className="label">💡 Explicação:</span>
                  <p>{answer.explanation}</p>
                </div>
              )}

              {/* Tempo gasto */}
              <div className="time_section">
                <span className="time_label">⏱️ Tempo gasto:</span>
                <span className="time_value">{formatTime(answer.timeSpent)}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="answer_details_actions">
          <button 
            className="quiz_back_btn" 
            onClick={() => setShowAnswerDetails(false)}
          >
            ← Voltar aos Resultados
          </button>
          <button 
            className="quiz_home_btn" 
            onClick={closeQuiz}
          >
            🏠 Voltar ao Feed
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz_feed">
      {/* Filtros */}
      <div className="quiz_filters">
        <div className="filter_row">
          <select
            className="quiz_filter_select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">Todos os Quizzes</option>
            <option value="active">Ativos</option>
            <option value="ended">Finalizados</option>
            <option value="paid">Pagos</option>
            <option value="free">Gratuitos</option>
          </select>

          <select
            className="quiz_filter_select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Grid de Quizzes */}
      <div className="quiz_grid">
        {quizzes.map((quiz) => (
          <div key={quiz._id} className="quiz_card">
            <div className="quiz_card_header">
              <Link to={`/profile/${quiz.user.username}`} className="quiz_author">
                <img src={quiz.user.picture} alt={quiz.user.username} />
                <span>{quiz.user.first_name} {quiz.user.last_name}</span>
              </Link>
              <FeedPostMenu
                userId={user.id}
                postUserId={quiz.user._id}
                postId={quiz._id}
                onEdit={() => handleEditPost(quiz)}
                onDelete={() => handlePostDeleted(quiz._id)}
              />
            </div>

            <div className="quiz_card_content">
              {quiz.quizData.coverImage && (
                <img src={quiz.quizData.coverImage} alt={quiz.quizData.title} className="quiz_cover" />
              )}

              <h3 className="quiz_title">{quiz.quizData.title}</h3>
              <p className="quiz_description">{quiz.quizData.description}</p>

              <div className="quiz_meta">
                <span className="quiz_category">{quiz.quizData.category}</span>
                {quiz.quizData.difficulty && (
                  <span className={`quiz_difficulty ${quiz.quizData.difficulty}`}>
                    {quiz.quizData.difficulty}
                  </span>
                )}
              </div>

              <div className="quiz_stats">
                <span>{quiz.quizData.questions?.length || 0} perguntas</span>
                <span>{quiz.quizData.participants?.length || 0} participantes</span>
                {quiz.quizData.timeLimit && <span>{quiz.quizData.timeLimit} min</span>}
              </div>

              {quiz.quizData.isPaid && (
                <div className="quiz_prize_info">
                  <span className="prize_label">Prêmio por Vencedor:</span>
                  <span className="prize_amount">
                    ${quiz.quizData.prizePerWinner} USD
                  </span>
                  <span className="prize_label_small">Top {quiz.quizData.topWinners}</span>
                </div>
              )}

              {quiz.quizData.hasParticipated ? (
                quiz.quizData.userStatus === "completed" ? (
                  <button
                    className="quiz_action_btn view_results"
                    onClick={() => viewResults(quiz._id)}
                  >
                    Ver Resultados
                  </button>
                ) : (
                  <button className="quiz_action_btn in_progress" disabled>
                    Em Progresso
                  </button>
                )
              ) : (
                <button
                  className="quiz_action_btn start"
                  onClick={() => joinQuiz(quiz)}
                >
                  Participar
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {hasMore && !loading && (
        <button
          className="quiz_load_more"
          onClick={() => {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchQuizzes(nextPage);
          }}
        >
          Carregar Mais
        </button>
      )}

      {loading && <div className="quiz_loader"></div>}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
